import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Minus, Plus, type LucideIcon } from "lucide-react";
import { Icon } from "../ui";
import { cx } from "../ui/cx";

export interface DiagramMarker {
  /** This marker. A position the book draws twice has two markers. */
  key: string;
  /** The position it belongs to; every marker of the selected position lights up. */
  position: number;
  label: string;
  x: number;
  y: number;
}

export interface DiagramViewerProps {
  image: { url: string; full_url: string };
  markers: DiagramMarker[];
  selected: number | null;
  /** Leave out for a plate that only shows where something is. */
  onSelect?: (position: number) => void;
  /** Phone sizing: 44px markers and 48px controls. */
  touch?: boolean;
  caption: string;
  /** Read out as the image's description. */
  alt: string;
}

const ZOOMS = [1, 1.5, 2, 2.5, 3];
/** A phone opens at 2×: at 100% a crowded plate's 44px markers cover each other. */
const TOUCH_START = 2;

/** Full numbered badge diameter, in rendered CSS px — the width a marker needs to not collide. */
const FULL = { touch: 44, desktop: 26 };

/**
 * Each marker's nearest-neighbor distance — the smallest center-to-center distance from it to any
 * other marker, in rendered CSS px, given the image box's current rendered size. Returns an array
 * parallel to `markers`. Markers are percentages of that box, so this is the same left/top math the
 * component already does. O(n²), trivial at catalogue scale (worst real plate: 48 markers, 1,128
 * pairs, well under a millisecond). A marker crowds only because of its own closest neighbor, never
 * because some unrelated corner of the plate is dense.
 */
function nearestNeighborDistances(markers: DiagramMarker[], boxWidth: number, boxHeight: number): number[] {
  const nn = new Array<number>(markers.length).fill(Infinity);
  for (let i = 0; i < markers.length; i++) {
    const ax = (markers[i].x / 100) * boxWidth;
    const ay = (markers[i].y / 100) * boxHeight;
    for (let j = i + 1; j < markers.length; j++) {
      const gap = Math.hypot(ax - (markers[j].x / 100) * boxWidth, ay - (markers[j].y / 100) * boxHeight);
      if (gap < nn[i]) nn[i] = gap;
      if (gap < nn[j]) nn[j] = gap;
    }
  }
  return nn;
}

/**
 * The exploded plate, read-only (design system DiagramViewer). Markers are percentages of the
 * picture, so they hold at any size. The web-sized copy shows until the plate is drawn larger
 * than it; only then is the original scan loaded (design doc §9).
 */
export function DiagramViewer({ image, markers, selected, onSelect, touch = false, caption, alt }: DiagramViewerProps) {
  const startIndex = touch ? ZOOMS.indexOf(TOUCH_START) : 0;
  const [zoomIndex, setZoomIndex] = useState(startIndex);
  const [status, setStatus] = useState<"loading" | "ready" | "failed">("loading");
  const [ratio, setRatio] = useState<number | null>(null);
  const [webWidth, setWebWidth] = useState<number | null>(null);
  const [sharp, setSharp] = useState(false);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);
  const frame = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const zoom = ZOOMS[zoomIndex];

  useEffect(() => {
    setStatus("loading");
    setZoomIndex(startIndex);
    setWebWidth(null);
    setSharp(false);
  }, [image.url, startIndex]);

  // The rendered width of the image box, which scales with both zoom and viewport. Watched rather
  // than computed so resize and zoom both feed the crowding check; debounced so a drag-resize
  // doesn't thrash it.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width == null) return;
      clearTimeout(timer);
      timer = setTimeout(() => setBoxWidth(width), 100);
    });
    observer.observe(el);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [status]);

  // A marker collapses to a small dot only when a full badge there would collide with its nearest
  // neighbor at this render size — a per-marker test, so an isolated position stays a numbered badge
  // even on a dense plate. The selected marker is always exempt (handled in render), so the
  // parts-list↔diagram link survives.
  const full = touch ? FULL.touch : FULL.desktop;
  const markerTier = useMemo<Map<string, "full" | "dot">>(() => {
    const tiers = new Map<string, "full" | "dot">();
    if (!boxWidth || !ratio || markers.length < 2) return tiers;
    const nn = nearestNeighborDistances(markers, boxWidth, boxWidth / ratio);
    markers.forEach((marker, i) => tiers.set(marker.key, nn[i] < full ? "dot" : "full"));
    return tiers;
  }, [markers, boxWidth, ratio, full]);

  // Zoomed in past the opening view, and drawn wider than the web copy's pixels: swap to the
  // scan, and keep it. The opening view always uses the web copy, even on a sharp phone screen.
  useEffect(() => {
    if (sharp || zoomIndex <= startIndex || !webWidth || !frame.current) return;
    if (zoom * frame.current.clientWidth * window.devicePixelRatio > webWidth * 1.1) setSharp(true);
  }, [zoom, zoomIndex, startIndex, webWidth, sharp]);

  const ready = status === "ready";

  return (
    <div className="overflow-hidden rounded-md border border-border-subtle bg-white shadow-xs">
      <div className={cx("flex gap-3 border-b border-border-subtle bg-surface-sunken px-4 py-2", touch ? "flex-col items-stretch" : "items-center")}>
        <span className="min-w-0 truncate type-eyebrow tracking-[var(--tracking-wider)] text-ink-500 uppercase">{caption}</span>
        <span className={cx("flex items-center gap-1", touch ? "justify-end" : "ml-auto")}>
          <ZoomButton label="Zoom out" icon={Minus} touch={touch} disabled={zoomIndex === 0 || !ready} onClick={() => setZoomIndex((i) => i - 1)} />
          <span aria-live="polite" className="w-12 text-center type-sku [font-size:var(--text-xs)] text-ink-600">
            {Math.round(zoom * 100)}%
          </span>
          <ZoomButton label="Zoom in" icon={Plus} touch={touch} disabled={zoomIndex === ZOOMS.length - 1 || !ready} onClick={() => setZoomIndex((i) => i + 1)} />
        </span>
      </div>

      {status === "failed" ? (
        <div role="alert" className="flex aspect-[4/3] flex-col items-center justify-center gap-3 bg-danger-100 p-8 text-center">
          <Icon icon={AlertTriangle} size={30} className="text-danger-700" />
          <span className="type-h4 text-danger-700">This diagram couldn't be loaded</span>
          <span className="max-w-sm type-small text-ink-600">Reload the page to try again. The parts list still works without it.</span>
        </div>
      ) : (
        // Zoomed in, the plate scrolls in its own frame so every corner stays reachable. Padded on
        // every side: a marker near an edge is centered on its point and overhangs it by half its
        // size (13px, or 22px on touch), which this would otherwise clip.
        <div ref={frame} className={cx("overflow-auto", touch ? "p-6" : "p-5", zoom > 1 && "max-h-[70dvh]")}>
          <div
            ref={box}
            className={cx("relative", !ready && "bg-[repeating-linear-gradient(45deg,var(--paper-0)_0_12px,#fff_12px_24px)]")}
            style={{ width: `${zoom * 100}%`, aspectRatio: ratio ?? "4 / 3" }}
          >
            <img
              src={sharp ? image.full_url : image.url}
              alt={alt}
              draggable={false}
              onLoad={(event) => {
                const img = event.currentTarget;
                setRatio(img.naturalWidth / img.naturalHeight);
                if (!sharp) setWebWidth(img.naturalWidth);
                setStatus("ready");
              }}
              onError={() => setStatus("failed")}
              className={cx("absolute inset-0 size-full select-none [transition:opacity_var(--duration-base)_var(--ease-out)]", ready ? "opacity-100" : "opacity-0")}
            />
            {ready &&
              markers.map((marker) => {
                const active = marker.position === selected;
                // A crowded marker collapses to a dot, but the selected marker always stays a full badge.
                const dot = markerTier.get(marker.key) === "dot" && !active;
                const style = { left: `${marker.x}%`, top: `${marker.y}%` };
                const look = cx(
                  "absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full [transition:var(--transition-control)]",
                  dot
                    ? // A dot keeps a 24px tap/click floor under its smaller visual (WCAG 2.5.8). A dot is
                      // never the selected marker — that one is forced to the full badge below.
                      "min-h-6 min-w-6 text-green-700"
                    : cx(
                        "border-2 px-1 type-sku shadow-sm",
                        touch ? "h-11 min-w-11 [font-size:var(--text-sm)]" : "h-[26px] min-w-[26px] [font-size:var(--text-xs)]",
                        active ? "z-[5] border-gold-500 bg-gold-500 text-ink-900" : "border-green-700 bg-white text-green-800",
                      ),
                );
                // The number can't fit in a dot, so it's dropped there; the position stays reachable by
                // tapping (the "Position N" sheet), the Pos. jump field and the parts list.
                const content = dot ? (
                  <span className={cx("rounded-full bg-current", touch ? "size-[10px]" : "size-[8px]")} />
                ) : (
                  marker.label
                );
                return onSelect ? (
                  <button
                    key={marker.key}
                    type="button"
                    aria-label={`Position ${marker.label}`}
                    aria-pressed={active}
                    onClick={() => onSelect(marker.position)}
                    className={cx(look, "cursor-pointer hover:border-gold-500")}
                    style={style}
                  >
                    {content}
                  </button>
                ) : (
                  <span key={marker.key} aria-hidden="true" className={look} style={style}>
                    {content}
                  </span>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

function ZoomButton({ label, icon, touch, disabled, onClick }: { label: string; icon: LucideIcon; touch: boolean; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        "flex cursor-pointer items-center justify-center rounded-sm border border-border-default bg-white text-ink-700 hover:bg-paper-1 disabled:cursor-not-allowed disabled:opacity-40",
        touch ? "size-12" : "size-8",
      )}
    >
      <Icon icon={icon} size={touch ? 20 : 16} />
    </button>
  );
}
